import isEmpty from "lodash/isEmpty";
import React, { ReactNode, useMemo } from "react";

import Box from "veneer/Box";
import { JollyRancherChip } from "veneer/Chip";
import Stack from "veneer/Stack";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import LogsConstants from "cortex/ui/src/fleet/constant/logs";
import { Collapsible } from "cortex/ui/src/ui/collapsible";
import { JsonViewer } from "cortex/ui/src/ui/json-viewer";
import { transformValues } from "cortex/ui/src/util/object";

type Extra = Record<string, unknown> | null | undefined;

const isAppProcessingEntry = (message: string) =>
  LogsConstants.APP_FAILURE_MESSAGES.includes(message);

const mapper = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value.replace(/\\/g, ""));
  } catch {
    return value;
  }
};

const formatExtra = (extra: Extra): Extra =>
  extra ? transformValues(extra as Record<string, unknown>, mapper) : extra;

function AppProcessingLabel({ extra }: { extra: Extra }) {
  const hasFailures = !!extra && !isEmpty((extra as any).failures);
  const icon = hasFailures
    ? "fa fa-times text-danger"
    : "fa fa-check text-success";
  const status = hasFailures
    ? LogsConstants.APP_FAILURE_DETECTED
    : LogsConstants.APP_FAILURE_NOT_DETECTED;

  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <i className={icon} />
      <Typography component="span" variant="body2">
        {status}
      </Typography>
    </Stack>
  );
}

function MessageLabel({ extra, message }: { extra: Extra; message: string }) {
  if (isAppProcessingEntry(message)) {
    return (
      <Stack
        alignItems="center"
        dataTestId="app-failure-label"
        direction="row"
        spacing={1}
      >
        <Typography component="span" fontWeight="bold" variant="body2">
          {message}{" "}
        </Typography>
        <AppProcessingLabel extra={extra} />
      </Stack>
    );
  }
  return (
    <Typography component="span" variant="body2">
      {message}
    </Typography>
  );
}

function CollapsibleMessage({
  extra,
  message,
}: {
  extra: Extra;
  message: string;
}) {
  const helpText =
    (LogsConstants.messages as Record<string, ReactNode> | undefined)?.[
      message
    ] ?? null;
  const itemCount = extra ? Object.keys(extra).length : 0;
  const label = (
    <Stack alignItems="center" direction="row">
      <MessageLabel extra={extra} message={message} />
      <Box ml={1}>
        <JollyRancherChip color="gray" label={`${itemCount} item(s)`} />
      </Box>
    </Stack>
  );
  return (
    <Collapsible label={label}>
      {helpText && (
        <Stack alignItems="center" direction="row" mb={2} spacing={1}>
          <i className="fa fa-info-circle" />
          <Typography component="span" variant="body2">
            {helpText}
          </Typography>
        </Stack>
      )}
      {!!extra && !isEmpty(extra) && <JsonViewer value={extra as object} />}
    </Collapsible>
  );
}

interface LogsMessageCellProps {
  message: string;
  rawExtra: Extra;
}

export default function LogsMessageCell({
  message,
  rawExtra,
}: LogsMessageCellProps) {
  const formattedExtra = useMemo(() => formatExtra(rawExtra), [rawExtra]);

  if (formattedExtra && !isEmpty(formattedExtra)) {
    const hasFailures = !isEmpty((formattedExtra as any).failures);
    const appEntry = isAppProcessingEntry(message);
    if (!appEntry || hasFailures) {
      return <CollapsibleMessage extra={formattedExtra} message={message} />;
    }
  }
  return (
    <OverflowTooltipTypography component="span" variant="body2">
      <MessageLabel extra={rawExtra} message={message} />
    </OverflowTooltipTypography>
  );
}
